import * as React from 'react';
import { Slot } from '@radix-ui/react-slot';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '@/lib/utils';
import { LucideIcon } from 'lucide-react';

export const buttonVariants = cva(
  'inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-xl text-sm font-medium ring-offset-background transition-all duration-200 focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0',
  {
    variants: {
      variant: {
        default:
          'gradient-primary border border-primary-400/30 text-white hover:brightness-110 active:scale-[0.98]',
        destructive:
          'bg-warning text-white hover:bg-warning/90 border border-warning/30 active:scale-[0.98] shadow-[0_4px_14px_rgba(243,18,96,0.35)]',
        outline:
          'border border-primary/30 bg-transparent text-white hover:bg-primary/10 hover:border-primary/50 active:scale-[0.98]',
        'outline-white':
          'border border-white/20 bg-transparent text-white hover:bg-white/5 active:scale-[0.98]',
        secondary:
          'bg-secondary text-white hover:bg-content-highlight border border-white/8 active:scale-[0.98]',
        ghost: 'text-white hover:bg-content-highlight active:scale-[0.98]',
        'ghost-dark': 'text-white hover:bg-content-dark active:scale-[0.98]',
        'ghost-primary':
          'text-primary-300 border border-primary/20 bg-primary/10 hover:bg-primary/15 hover:border-primary/35 active:scale-[0.98]',
        'ghost-primary-dark':
          'text-white bg-primary/12 border border-primary/25 hover:bg-primary/20 active:scale-[0.98]',
        'ghost-success': 'text-white hover:bg-green-800/80 active:scale-[0.98]',
        'ghost-success-glow': 'text-white hover:bg-green-600/90 active:scale-[0.98]',
        'ghost-danger': 'text-white hover:bg-red-800/80 active:scale-[0.98]',
        'ghost-danger-glow': 'text-white hover:bg-red-600/90 active:scale-[0.98]',
        'ghost-light':
          'bg-content-highlight text-white border border-white/8 hover:bg-content-dark active:scale-[0.98]',
        link: 'text-primary-400 underline-offset-4 hover:underline hover:text-primary-300',
      },
      size: {
        default: 'h-9 px-3.5 py-2 text-sm',
        sm: 'h-8 px-2.5 text-xs',
        lg: 'h-10 px-6 text-sm',
        xl: 'h-12 px-10 text-lg',
        '2xl': 'h-14 px-14 text-lg',
        '3xl': 'h-16 px-16 text-lg',
        icon: 'h-9 w-9',
        iconAlign: 'h-8 w-8',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'default',
    },
  },
);

export interface ButtonProps extends React.ComponentPropsWithRef<'button'>, VariantProps<typeof buttonVariants> {
  asChild?: boolean;
}

const Button = ({ ref, className, variant, size, asChild = false, ...props }: ButtonProps) => {
  const Comp = asChild ? Slot : 'button';
  return (
    <Comp
      className={cn(buttonVariants({ variant, size, className }))}
      ref={ref}
      onClick={(e) => {
        e.stopPropagation();
        props.onClick?.(e);
      }}
      {...props}
    >
      {props.children}
    </Comp>
  );
};
Button.displayName = 'Button';

export interface IconButtonProps extends React.ComponentPropsWithRef<'button'>, VariantProps<typeof buttonVariants> {
  asChild?: boolean;
  leftIcon?: LucideIcon;
  rightIcon?: LucideIcon;
  icon?: LucideIcon;
}

const IconButton = ({ ref, className, variant, size, leftIcon: LeftIcon, rightIcon: RightIcon, icon: Icon, children, ...props }: IconButtonProps) => {
  return (
    <Button className={cn('relative', className)} variant={variant} size={size} ref={ref} {...props}>
      {Icon ? (
        <Icon className="w-5 h-5" />
      ) : (
        <>
          {LeftIcon && <LeftIcon className="w-5 h-5" />}
          {children}
          {RightIcon && <RightIcon className="w-5 h-5" />}
        </>
      )}
    </Button>
  );
};
IconButton.displayName = 'IconButton';

export { Button, IconButton };
